import { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import {
  detectRestrictedWords,
  mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";

type HighlightedNarrativeProps = {
  text: string;
  restrictedWords?: RestrictedWord[];
  className?: string;
};

export function HighlightedNarrative({
  text,
  restrictedWords,
  className,
}: HighlightedNarrativeProps) {
  const matches = useMemo(
    () =>
      restrictedWords?.length
        ? mergeOverlappingMatches(detectRestrictedWords(text, restrictedWords))
        : [],
    [text, restrictedWords],
  );

  if (!matches.length) {
    return <span className={className}>{text}</span>;
  }

  const segments: React.ReactNode[] = [];
  let cursor = 0;

  for (const m of matches) {
    if (m.start > cursor) {
      segments.push(
        <span key={`t-${cursor}`}>{text.slice(cursor, m.start)}</span>,
      );
    }
    segments.push(
      <TooltipProvider key={`m-${m.start}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <mark
              className={cn(
                "rounded-sm px-0.5 cursor-default underline decoration-wavy",
                m.severity === "block"
                  ? "bg-red-200/70 dark:bg-red-500/25 text-inherit decoration-red-500/70"
                  : "bg-amber-200/70 dark:bg-amber-500/25 text-inherit decoration-amber-500/70",
              )}
            >
              {text.slice(m.start, m.end)}
            </mark>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className={cn(
                  "inline-block h-2 w-2 rounded-full shrink-0",
                  m.severity === "block" ? "bg-red-500" : "bg-amber-500",
                )}
              />
              <span
                className={cn(
                  "text-[10px] font-medium uppercase tracking-wide",
                  m.severity === "block"
                    ? "text-red-600 dark:text-red-400"
                    : "text-amber-600 dark:text-amber-400",
                )}
              >
                {m.severity === "block" ? "Blocked" : "Warning"}
              </span>
            </div>
            <p className="text-xs font-medium text-foreground">{m.displayName}</p>
            <p className="text-xs text-muted-foreground">
              {m.suggestion ? (
                <>Suggested: <span className="font-medium text-foreground">{m.suggestion}</span></>
              ) : (
                "No replacement suggested"
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>,
    );
    cursor = m.end;
  }
  if (cursor < text.length) {
    segments.push(<span key={`t-${cursor}`}>{text.slice(cursor)}</span>);
  }

  return <span className={className}>{segments}</span>;
}

export default HighlightedNarrative;
